import React, { useState, useEffect, useRef, useMemo } from 'react'
import ReactDOM from 'react-dom'
import { inject, observer } from 'mobx-react'
import { withTranslation } from 'react-i18next'
import { Map, GeoJSON, WMSTileLayer } from 'react-leaflet'
import L from 'leaflet'
import densityLayers from '../../densityLayers'
import GoogleLayer from '../map/GoogleLayer'
import constants from '../../constants'
import api from '../../api'
import { BASE_MAP_OPTIONS } from './ShareImageMapSettings'
import { formatCompactNumber, geoJSONBounds } from '../../utils'
import { Mail01Icon } from '@hugeicons/core-free-icons'
import { HugeiconsIcon } from '@hugeicons/react'
import Wkt from 'wicket/wicket'

const CloseIcon = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M12 4L4 12M4 4l8 8' />
  </svg>
)

const MailSendIcon = () => (
  <HugeiconsIcon
    icon={Mail01Icon}
    size={24}
    color='currentColor'
    strokeWidth={1.5}
  />
)

// Resolve GeoServer density / heatmap configuration (URL, layer ID, workspace, format, version)
const resolveHeatmapConfig = (uiStore) => {
  const layerId = (uiStore && uiStore.densityLayer && uiStore.densityLayer !== 'none')
    ? uiStore.densityLayer
    : (densityLayers.layers && densityLayers.layers[0] ? densityLayers.layers[0].id : 'population_both_cohorts:landscan_pop_2016')

  const layer = (densityLayers.layers && densityLayers.layers.find(l => l.id === layerId)) || (densityLayers.layers && densityLayers.layers[0]) || { workspace: 'population_both_cohorts', id: 'population_both_cohorts:landscan_pop_2016' }
  const workspace = layer.workspace || 'population_both_cohorts'
  let server = densityLayers.server || `${constants.GEOSERVER}`
  if (!server.endsWith('/')) {
    server += '/'
  }
  const year = (uiStore && uiStore.currentYear) || 2016

  let url = ''
  if (uiStore && uiStore.densityLayer && uiStore.densityLayer !== 'none' && uiStore.densityLayerUrl) {
    url = uiStore.densityLayerUrl
  } else {
    url = year === 2016 ? `${server}${workspace}/wms` : `${server}${workspace}_${year}/wms`
  }

  return {
    key: densityLayers.key || 'density',
    layerId,
    workspace,
    url,
    version: densityLayers.version || '1.3.0',
    format: densityLayers.format || 'image/png'
  }
}

// Bounding box in EPSG:3857 for tile coordinates (x, y, z)
const getTileBboxEPSG3857 = (x, y, z) => {
  const earthCircumference = 40075016.68557849
  const numTiles = Math.pow(2, z)
  const tileMeters = earthCircumference / numTiles
  const originShift = 20037508.342789244

  const minX = -originShift + x * tileMeters
  const maxX = minX + tileMeters
  const maxY = originShift - y * tileMeters
  const minY = maxY - tileMeters

  return `${minX},${minY},${maxX},${maxY}`
}

// GeoServer WMS tile URL for EPSG:3857
const getHeatmapTileUrl = (x, y, z, config) => {
  const bbox = getTileBboxEPSG3857(x, y, z)
  const isV13 = config.version && config.version.startsWith('1.3')
  const crsKey = isV13 ? 'CRS' : 'SRS'
  const separator = config.url.includes('?') ? '&' : '?'
  return `${config.url}${separator}SERVICE=WMS&REQUEST=GetMap&LAYERS=${config.layerId}&STYLES=&FORMAT=image/png&TRANSPARENT=true&VERSION=${encodeURIComponent(config.version || '1.3.0')}&WIDTH=256&HEIGHT=256&${crsKey}=EPSG:3857&BBOX=${bbox}&_cb=1`
}

// Convert WGS84 Lat/Lng to EPSG:3857 Web Mercator meters
const latLngToEPSG3857 = (lat, lng) => {
  const originShift = 20037508.342789244
  const x = (lng * originShift) / 180
  let y = Math.log(Math.tan(((90 + Math.max(-85.05112878, Math.min(85.05112878, lat))) * Math.PI) / 360)) / (Math.PI / 180)
  y = (y * originShift) / 180
  return { x, y }
}

// Full-viewport fallback WMS GetMap URL in EPSG:3857
const getHeatmapViewportUrl = (bounds, width, height, config) => {
  const sw = latLngToEPSG3857(bounds.getSouth(), bounds.getWest())
  const ne = latLngToEPSG3857(bounds.getNorth(), bounds.getEast())
  const bbox = `${sw.x},${sw.y},${ne.x},${ne.y}`
  const isV13 = config.version && config.version.startsWith('1.3')
  const crsKey = isV13 ? 'CRS' : 'SRS'
  const separator = config.url.includes('?') ? '&' : '?'
  return `${config.url}${separator}SERVICE=WMS&REQUEST=GetMap&LAYERS=${config.layerId}&STYLES=&FORMAT=image/png&TRANSPARENT=true&VERSION=${encodeURIComponent(config.version || '1.3.0')}&WIDTH=${Math.round(width)}&HEIGHT=${Math.round(height)}&${crsKey}=EPSG:3857&BBOX=${bbox}&_cb=1`
}

const isIsochroneItem = (elem, geo) => {
  if (!elem && !geo) return false
  const typeStr = String(elem?.type || elem?.shape?.type || elem?.layer || '').toLowerCase()
  if (
    typeStr === 'isochrone' ||
    typeStr === 'isochrone_shape' ||
    typeStr === 'bulk_isochrone'
  ) {
    return true
  }
  if (elem?.travelTime != null || elem?.buffer?.travelTime != null) {
    return true
  }
  if (elem?.modeOfTransportation != null || elem?.buffer?.modeOfTransportation != null) {
    return true
  }
  if (typeof elem?.name === 'string' && /isochrone/i.test(elem.name)) {
    return true
  }
  if (typeof elem?.shape?.name === 'string' && /isochrone/i.test(elem.shape.name)) {
    return true
  }
  const geoProps = geo?.properties || (geo?.features && geo.features[0]?.properties) || {}
  if (geoProps.isochrone || geoProps.contour != null || geoProps.metric != null) {
    return true
  }
  if (typeof geoProps.name === 'string' && /isochrone/i.test(geoProps.name)) {
    return true
  }
  return false
}

const parseBorderString = (str) => {
  if (!str || typeof str !== 'string') return null
  const trimmed = str.trim()
  if (!trimmed || trimmed === 'none' || trimmed === '0' || trimmed === 'hidden') {
    return { none: true }
  }
  if (/^#([0-9a-f]{3,8})$/i.test(trimmed) || /^(rgb|hsl)a?\(.+?\)$/i.test(trimmed)) {
    return { color: trimmed }
  }
  if (/^[a-z]+$/i.test(trimmed)) {
    return { color: trimmed }
  }
  const parts = trimmed.split(/\s+/)
  let width = null
  let style = null
  let color = null
  for (const p of parts) {
    if (/^\d+(\.\d+)?(px)?$/i.test(p)) {
      width = parseFloat(p)
    } else if (/^(solid|dashed|dotted|double)$/i.test(p)) {
      style = p.toLowerCase()
    } else if (/^#|^rgb|^hsl|[a-z]+/i.test(p)) {
      color = p
    }
  }
  return {
    color,
    width,
    dashArray: style === 'dashed' ? '7' : (style === 'dotted' ? '3, 3' : null)
  }
}

const resolveGeometryBorder = (elem, geo, baseColor = '#0E7490', dataStore = null) => {
  if (!elem && !geo) {
    return { hasBorder: false, borderColor: baseColor, borderWidth: 2.5, borderDashArray: null, borderOpacity: 1 }
  }

  const geoProps = geo?.properties || (geo?.features && geo.features[0]?.properties) || {}
  const shape = elem?.shape || {}

  const rawBorder = elem?.border ?? shape.border ?? geoProps.border
  const rawBorderColor = elem?.borderColor ?? elem?.border_color ?? shape.borderColor ?? shape.border_color ??
    elem?.strokeColor ?? elem?.stroke_color ?? shape.strokeColor ?? shape.stroke_color ??
    elem?.stroke ?? shape.stroke ?? geoProps.borderColor ?? geoProps.border_color ?? geoProps.strokeColor ?? geoProps.stroke
  const rawBorderWidth = elem?.borderWidth ?? elem?.border_width ?? shape.borderWidth ?? shape.border_width ??
    elem?.strokeWidth ?? elem?.stroke_width ?? shape.strokeWidth ?? shape.stroke_width ??
    geoProps.borderWidth ?? geoProps.border_width ?? geoProps['stroke-width']
  const rawDashArray = elem?.borderDashArray ?? elem?.border_dash_array ?? elem?.dashArray ?? elem?.strokeDasharray ??
    elem?.stroke_dasharray ?? shape.dashArray ?? shape.strokeDasharray ??
    geoProps.borderDashArray ?? geoProps.dashArray ?? geoProps['stroke-dasharray']
  const rawBorderOpacity = elem?.borderOpacity ?? elem?.border_opacity ?? elem?.strokeOpacity ??
    shape.borderOpacity ?? geoProps.borderOpacity ?? geoProps['stroke-opacity']

  let inactiveColor = null
  const colorId = elem?.color_id != null ? elem.color_id : (shape.color_id != null ? shape.color_id : null)
  if (colorId != null && dataStore?.markerColors) {
    const mc = dataStore.markerColors.find(m => String(m.id) === String(colorId))
    if (mc && mc.inactive_color) inactiveColor = mc.inactive_color
  }
  if (!inactiveColor && (elem?.inactive_color || shape.inactive_color)) {
    inactiveColor = elem?.inactive_color || shape.inactive_color
  }
  if (!inactiveColor && elem?.color && typeof elem.color === 'object' && elem.color.inactive_color) {
    inactiveColor = elem.color.inactive_color
  }

  let parsedBorder = null
  if (rawBorder != null) {
    if (typeof rawBorder === 'string') {
      parsedBorder = parseBorderString(rawBorder)
    } else if (typeof rawBorder === 'object') {
      parsedBorder = rawBorder
    } else if (rawBorder === true) {
      parsedBorder = { enabled: true }
    } else if (rawBorder === false) {
      parsedBorder = { none: true }
    }
  }

  if (parsedBorder && parsedBorder.none) {
    return { hasBorder: false, borderColor: baseColor, borderWidth: 0, borderDashArray: null, borderOpacity: 0 }
  }

  const isIso = isIsochroneItem(elem, geo)

  const hasExplicitBorder = Boolean(
    parsedBorder ||
    rawBorderColor ||
    rawBorderWidth != null ||
    rawDashArray != null ||
    elem?.hasBorder ||
    shape.hasBorder ||
    geoProps.hasBorder
  )

  if (hasExplicitBorder || isIso) {
    const finalBorderColor =
      parsedBorder?.color ||
      rawBorderColor ||
      (isIso ? (inactiveColor || '#ff5500') : (inactiveColor || '#ff5500' || baseColor))

    const finalBorderWidth = Number(
      parsedBorder?.width ??
      rawBorderWidth ??
      (isIso ? 3 : 3)
    )

    const finalDashArray =
      parsedBorder?.dashArray ||
      rawDashArray ||
      (isIso ? '7' : null)

    const finalOpacity = rawBorderOpacity != null ? Number(rawBorderOpacity) : 1

    return {
      hasBorder: true,
      borderColor: finalBorderColor || baseColor,
      borderWidth: !isNaN(finalBorderWidth) && finalBorderWidth >= 0 ? finalBorderWidth : 3,
      borderDashArray: finalDashArray ? String(finalDashArray) : null,
      borderOpacity: !isNaN(finalOpacity) ? finalOpacity : 1
    }
  }

  return {
    hasBorder: false,
    borderColor: baseColor,
    borderWidth: 2.5,
    borderDashArray: null,
    borderOpacity: 1
  }
}

function drawGeoJSONOnCanvas (ctx, geojson, map, scaleX, scaleY, fillColor, strokeColor, fillOpacity, strokeWidth = 2.5, customLabel = '', dashArray = null, strokeOpacity = 1) {
  if (!geojson || !map) return

  const drawPolygonRings = (rings) => {
    if (!rings || !rings.length) return
    ctx.beginPath()
    for (let r = 0; r < rings.length; r++) {
      const ring = rings[r]
      for (let i = 0; i < ring.length; i++) {
        const [lng, lat] = ring[i]
        const pt = map.latLngToContainerPoint([lat, lng])
        const x = pt.x * scaleX
        const y = pt.y * scaleY
        if (i === 0) {
          ctx.moveTo(x, y)
        } else {
          ctx.lineTo(x, y)
        }
      }
      ctx.closePath()
    }

    ctx.save()
    ctx.fillStyle = fillColor
    ctx.globalAlpha = fillOpacity
    ctx.fill('evenodd')
    ctx.restore()

    ctx.save()
    if (dashArray) {
      const dashes = (typeof dashArray === 'string' ? dashArray.split(/[ ,]+/).map(Number) : (Array.isArray(dashArray) ? dashArray : [Number(dashArray)]))
        .filter(n => !isNaN(n) && n > 0)
        .map(n => n * scaleX)
      if (dashes.length > 0) {
        ctx.setLineDash(dashes)
      }
    }
    ctx.strokeStyle = strokeColor
    ctx.lineWidth = strokeWidth * scaleX
    if (strokeOpacity != null && !isNaN(Number(strokeOpacity))) {
      ctx.globalAlpha = Number(strokeOpacity)
    }
    ctx.lineJoin = 'round'
    ctx.lineCap = 'round'
    ctx.stroke()
    ctx.restore()
  }

  const drawLine = (coords) => {
    if (!coords || !coords.length) return
    ctx.beginPath()
    for (let i = 0; i < coords.length; i++) {
      const [lng, lat] = coords[i]
      const pt = map.latLngToContainerPoint([lat, lng])
      const x = pt.x * scaleX
      const y = pt.y * scaleY
      if (i === 0) {
        ctx.moveTo(x, y)
      } else {
        ctx.lineTo(x, y)
      }
    }
    ctx.save()
    if (dashArray) {
      const dashes = (typeof dashArray === 'string' ? dashArray.split(/[ ,]+/).map(Number) : (Array.isArray(dashArray) ? dashArray : [Number(dashArray)]))
        .filter(n => !isNaN(n) && n > 0)
        .map(n => n * scaleX)
      if (dashes.length > 0) {
        ctx.setLineDash(dashes)
      }
    }
    ctx.strokeStyle = strokeColor
    ctx.lineWidth = strokeWidth * scaleX
    if (strokeOpacity != null && !isNaN(Number(strokeOpacity))) {
      ctx.globalAlpha = Number(strokeOpacity)
    }
    ctx.lineJoin = 'round'
    ctx.lineCap = 'round'
    ctx.stroke()
    ctx.restore()
  }

  const drawPoint = (coords, featureProps) => {
    if (!coords || coords.length < 2) return
    const [lng, lat] = coords
    const pt = map.latLngToContainerPoint([lat, lng])
    const x = pt.x * scaleX
    const y = pt.y * scaleY

    // Determine inner circle color vs outer pin
    const pointColor = fillColor || (featureProps && (featureProps.color || featureProps.fillColor)) || '#FFFFFF'
    const innerColor = (pointColor && pointColor !== '#102a43' && pointColor !== '#0a2c4f' && pointColor !== '#162a3f' && pointColor !== '#18262B' && pointColor !== 'rgba(10, 44, 79, 0.6)') ? pointColor : '#FFFFFF'

    // Draw high-resolution pointer matching dashboard fa-drop
    ctx.save()
    const pinScale = Math.max(1.0, scaleX * 0.85)
    ctx.translate(x, y)

    const dh = 36 * pinScale

    ctx.save()
    ctx.shadowColor = 'rgba(0, 0, 0, 0.35)'
    ctx.shadowBlur = 5 * pinScale
    ctx.shadowOffsetY = 2 * pinScale

    const scaleFactor = dh / 225.93
    ctx.scale(scaleFactor, scaleFactor)
    ctx.translate(0, -225.93)
    ctx.translate(112.965, 112.965)
    ctx.rotate(Math.PI)
    ctx.translate(-112.965, -112.965)

    // Outer Dark Navy Drop Pin Body
    const dropPath = new Path2D('M158.657,43.34c-21.05-24.471-41.786-40.395-42.658-41.061L113.011,0l-3.021,2.234 c-0.873,0.646-21.634,16.104-42.701,40.347c-28.617,32.931-43.127,64.741-43.127,94.547c0,48.966,39.836,88.802,88.802,88.802 s88.803-39.836,88.803-88.802C201.767,108.149,187.262,76.594,158.657,43.34z')
    ctx.fillStyle = '#102a43'
    ctx.fill(dropPath)

    // Inner Circle (colored or white)
    ctx.beginPath()
    ctx.arc(112.965, 138, 44, 0, Math.PI * 2)
    ctx.fillStyle = innerColor
    ctx.fill()
    ctx.restore()

    // Draw marker label pill above marker if name / label exists
    const markerName = (featureProps && (featureProps.name || featureProps.title || featureProps.label)) || customLabel || ''
    if (markerName && typeof markerName === 'string' && markerName.trim()) {
      const cleanName = markerName.trim()
      const fontSize = Math.max(10, Math.round(11 * pinScale))
      ctx.font = `600 ${fontSize}px 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`

      // Measure & truncate label text if too long
      let displayName = cleanName
      const maxTextWidth = 110 * pinScale
      if (ctx.measureText(displayName).width > maxTextWidth) {
        while (displayName.length > 3 && ctx.measureText(`${displayName}...`).width > maxTextWidth) {
          displayName = displayName.slice(0, -1)
        }
        displayName = `${displayName}...`
      }

      const textMetrics = ctx.measureText(displayName)
      const textWidth = textMetrics.width
      const padX = 6 * pinScale
      const pillH = Math.max(16, Math.round(18 * pinScale))
      const pillW = textWidth + padX * 2
      const pillX = -pillW / 2
      const pillY = -dh - (3 * pinScale) - pillH
      const pillRadius = 3 * pinScale

      ctx.save()
      ctx.shadowColor = 'rgba(0, 0, 0, 0.35)'
      ctx.shadowBlur = 3 * pinScale
      ctx.shadowOffsetY = 1 * pinScale

      // Background rounded pill
      ctx.fillStyle = '#FFFFFF'
      ctx.beginPath()
      if (ctx.roundRect) {
        ctx.roundRect(pillX, pillY, pillW, pillH, pillRadius)
      } else {
        ctx.rect(pillX, pillY, pillW, pillH)
      }
      ctx.fill()
      ctx.restore()

      // Text inside pill
      ctx.save()
      ctx.fillStyle = '#18262B'
      ctx.font = `600 ${fontSize}px 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(displayName, 0, pillY + pillH / 2)
      ctx.restore()
    }

    ctx.restore()
  }

  const handleGeometry = (geom, props) => {
    if (!geom) return
    if (geom.type === 'Polygon') {
      drawPolygonRings(geom.coordinates)
    } else if (geom.type === 'MultiPolygon') {
      geom.coordinates.forEach(poly => drawPolygonRings(poly))
    } else if (geom.type === 'LineString') {
      drawLine(geom.coordinates)
    } else if (geom.type === 'MultiLineString') {
      geom.coordinates.forEach(line => drawLine(line))
    } else if (geom.type === 'Point') {
      drawPoint(geom.coordinates, props)
    } else if (geom.type === 'MultiPoint') {
      geom.coordinates.forEach(pt => drawPoint(pt, props))
    } else if (geom.type === 'GeometryCollection') {
      geom.geometries.forEach(g => handleGeometry(g, props))
    }
  }

  if (geojson.type === 'FeatureCollection') {
    geojson.features.forEach(f => handleGeometry(f.geometry, f.properties))
  } else if (geojson.type === 'Feature') {
    handleGeometry(geojson.geometry, geojson.properties)
  } else {
    handleGeometry(geojson, geojson.properties)
  }
}

const ShareByEmailModal = ({
  isOpen,
  onClose,
  item,
  preGeneratedImage,
  selectionStore,
  rightSidebarStore,
  uiStore,
  dataStore,
  t,
  onSuccess
}) => {
  const mapRef = useRef(null)
  const [viewport, setViewport] = useState({ center: [51.505, -0.09], zoom: 12 })

  // Form fields
  const [firstName, setFirstName] = useState('')
  const [surname, setSurname] = useState('')
  const [senderEmail, setSenderEmail] = useState('')
  const [recipientEmail, setRecipientEmail] = useState('')
  const [subject, setSubject] = useState('Take a look at this location')
  const [message, setMessage] = useState('')
  const [firstNameError, setFirstNameError] = useState('')
  const [surnameError, setSurnameError] = useState('')
  const [senderEmailError, setSenderEmailError] = useState('')
  const [recipientError, setRecipientError] = useState('')
  const [subjectError, setSubjectError] = useState('')
  const [messageError, setMessageError] = useState('')
  const [formError, setFormError] = useState('')
  const [isSending, setIsSending] = useState(false)

  const wasOpenRef = useRef(false)

  // Initialize sender details from logged in user only when modal opens
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      wasOpenRef.current = true
      const u = uiStore ? uiStore.user : null
      const email = (u && u.email) || (uiStore && uiStore.email) || ''
      setSenderEmail(email)

      let fn = ''
      let ln = ''
      if (u && u.first_name) fn = u.first_name
      if (u && u.last_name) ln = u.last_name
      if (!fn && !ln && email) {
        const namePart = email.split('@')[0]
        const parts = namePart.split(/[._-]/)
        if (parts.length > 0) fn = parts[0].charAt(0).toUpperCase() + parts[0].slice(1)
        if (parts.length > 1) ln = parts[1].charAt(0).toUpperCase() + parts[1].slice(1)
      }
      setFirstName(fn)
      setSurname(ln)
      setRecipientEmail('')
      setFirstNameError('')
      setSurnameError('')
      setSenderEmailError('')
      setRecipientError('')
      setSubjectError('')
      setMessageError('')
      setFormError('')
      setMessage('')
      setSubject('Take a look at this location')
    } else if (!isOpen) {
      wasOpenRef.current = false
    }
  }, [isOpen])

  const resolveItemName = (elem) => {
    if (!elem) return ''
    if (elem.name && typeof elem.name === 'string' && elem.name.trim() && elem.name !== 'Selected Area' && elem.name !== 'Selected Location') return elem.name.trim()
    if (elem.title && typeof elem.title === 'string' && elem.title.trim() && elem.title !== 'Selected Area' && elem.title !== 'Selected Location') return elem.title.trim()
    if (elem.shape_name && typeof elem.shape_name === 'string' && elem.shape_name.trim()) return elem.shape_name.trim()
    if (elem.displayName && typeof elem.displayName === 'string' && elem.displayName.trim()) return elem.displayName.trim()
    if (elem.zip_code) return String(elem.zip_code).trim()
    if (elem.zipcode) return String(elem.zipcode).trim()
    if (elem.zip) return String(elem.zip).trim()
    if (elem.code) return String(elem.code).trim()
    if (elem.postcode) return String(elem.postcode).trim()
    if (elem.postal_code) return String(elem.postal_code).trim()
    if (elem.properties) {
      const p = elem.properties
      if (p.name && typeof p.name === 'string' && p.name.trim()) return p.name.trim()
      if (p.title && typeof p.title === 'string' && p.title.trim()) return p.title.trim()
      if (p.NAME && typeof p.NAME === 'string' && p.NAME.trim()) return p.NAME.trim()
      if (p.zip_code) return String(p.zip_code).trim()
      if (p.zipcode) return String(p.zipcode).trim()
      if (p.ZIP) return String(p.ZIP).trim()
      if (p.ZIPCODE) return String(p.ZIPCODE).trim()
      if (p.ZCTA5CE10) return String(p.ZCTA5CE10).trim()
      if (p.code) return String(p.code).trim()
      if (p.postalCode) return String(p.postalCode).trim()
    }
    return ''
  }

  // Extract color for any shape/boundary/group
  const resolveColor = (elem) => {
    if (!elem) return '#0E7490'

    // 1. If selectionStore has an active selected color for the current item / edited item
    const isSelectionItem = elem === selectionStore?.item ||
      (selectionStore?.colorEditItem && (elem === selectionStore.colorEditItem || elem.targetGroupId === selectionStore.colorEditItem.pk))
    if (isSelectionItem && selectionStore?.selectedColor != null) {
      if (typeof selectionStore.selectedColor === 'string' && (selectionStore.selectedColor.startsWith('#') || selectionStore.selectedColor.startsWith('rgb') || selectionStore.selectedColor.startsWith('hsl'))) {
        return selectionStore.selectedColor
      }
      const mc = dataStore?.markerColors?.find(m => String(m.id) === String(selectionStore.selectedColor))
      if (mc && mc.active_color) return mc.active_color
    }

    // 2. Shape status color (shape_status assignment)
    if (elem.shape_status && dataStore?.shapeStatuses) {
      const status = dataStore.shapeStatuses.find(s => s.id === elem.shape_status)
      if (status && status.color) return status.color
    }

    // 3. Color ID from markerColors
    const colorId = elem.color_id != null ? elem.color_id : (typeof elem.color === 'number' ? elem.color : null)
    if (colorId != null && dataStore?.markerColors) {
      const mc = dataStore.markerColors.find(m => String(m.id) === String(colorId))
      if (mc && mc.active_color) return mc.active_color
    }

    // 4. SavedMarker or Marker object getColor or color object
    if (elem.getColor && typeof elem.getColor === 'object' && elem.getColor.active_color) {
      return elem.getColor.active_color
    }
    if (elem.color && typeof elem.color === 'object') {
      if (elem.color.active_color) return elem.color.active_color
      if (elem.color.color) return elem.color.color
    }

    // 5. POI bucket colors for markers
    if (elem.is_competitor) return '#FAD165'
    if (elem.is_ally) return '#5EB1BC'
    if (elem.is_general) return '#0a2c4f'

    // 6. Direct color string (#hex, rgb, hsl)
    if (typeof elem.color === 'string' && (elem.color.startsWith('#') || elem.color.startsWith('rgb') || elem.color.startsWith('hsl'))) {
      return elem.color
    }
    if (typeof elem.color_code === 'string' && elem.color_code.startsWith('#')) return elem.color_code
    if (typeof elem.hex === 'string' && elem.hex.startsWith('#')) return elem.hex

    // 7. Computed getColor getter or method (string)
    if (typeof elem.getColor === 'string') return elem.getColor
    if (typeof elem.getColor === 'function') {
      try {
        const c = elem.getColor()
        if (typeof c === 'string') return c
        if (c && typeof c === 'object' && c.active_color) return c.active_color
      } catch (e) {}
    }

    return '#0E7490'
  }

  const resolveTransparency = (elem) => {
    const isSelectionItem = elem === selectionStore?.item ||
      (selectionStore?.colorEditItem && (elem === selectionStore.colorEditItem || elem.targetGroupId === selectionStore.colorEditItem.pk))
    if (isSelectionItem && selectionStore?.selectedTransparency != null) {
      return Math.max(0.1, Math.min(1, (Number(selectionStore.selectedTransparency) || 50) / 100))
    }
    const tr = elem?.transparency != null ? elem.transparency : (elem?.color_transparency != null ? elem.color_transparency : 50)
    return Math.max(0.1, Math.min(1, (Number(tr) || 50) / 100))
  }

  const extractGeoJSON = (elem) => {
    if (!elem) return null
    let g = elem.geoJSON || elem.region || elem.geometry || elem.geometries || elem.polygon || elem.the_geom || elem.isochronePolygonGeoJson || (elem.shape && (elem.shape.geoJSON || elem.shape.region || elem.shape.polygon || elem.shape.the_geom))
    if (typeof g === 'string') {
      try {
        g = JSON.parse(g)
      } catch (e) {
        try {
          g = new Wkt.Wkt().read(g).toJson()
        } catch (err) {
          g = null
        }
      }
    }
    if (!g || typeof g !== 'object' || Array.isArray(g) || !g.type) g = null
    const name = resolveItemName(elem) || elem.name || elem.title || (g && g.properties && (g.properties.name || g.properties.title)) || (isIsochroneItem(elem, g) ? 'Isochrone' : '')
    if (g) {
      if (typeof g === 'object' && !Array.isArray(g)) {
        if (!g.properties) g.properties = {}
        if (name && !g.properties.name) g.properties.name = name
      }
      return g
    }

    // Check position object (e.g. SavedMarker, Marker, POI search results)
    const lat = elem.lat ?? elem.latitude ?? elem.position?.latLng?.lat ?? elem.position?.latitude ?? elem.location?.lat ?? elem.location?.latitude
    const lng = elem.lng ?? elem.longitude ?? elem.position?.latLng?.lng ?? elem.position?.longitude ?? elem.location?.lng ?? elem.location?.longitude

    if (lat != null && lng != null && !isNaN(Number(lat)) && !isNaN(Number(lng))) {
      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [Number(lng), Number(lat)]
        },
        properties: {
          name,
          title: name,
          isMarker: true,
          icon_url: elem.icon_url || null
        }
      }
    }
    return null
  }

  const geometries = useMemo(() => {
    const list = []
    const seenIds = new Set()

    const addGeometry = (elem) => {
      if (!elem) return
      const geo = extractGeoJSON(elem)
      if (!geo) return
      const elemId = elem.id || elem.inner_id || elem.remote_db_id || elem.pk || (elem.shape && (elem.shape.id || elem.shape.inner_id)) || Math.random().toString()
      if (seenIds.has(elemId)) return
      seenIds.add(elemId)

      const elemName = resolveItemName(elem) || elem.name || elem.title || geo.properties?.name || geo.properties?.title || (isIsochroneItem(elem, geo) ? 'Isochrone' : '')
      const color = resolveColor(elem)
      const transparency = resolveTransparency(elem)
      const border = resolveGeometryBorder(elem, geo, color, dataStore)
      list.push({
        id: elemId,
        name: elemName,
        label: elemName,
        geoJSON: geo,
        color,
        transparency,
        hasBorder: border.hasBorder,
        borderColor: border.borderColor,
        borderWidth: border.borderWidth,
        borderDashArray: border.borderDashArray,
        borderOpacity: border.borderOpacity,
        isPoint: geo.geometry?.type === 'Point' || geo.type === 'Point'
      })
    }

    const processItem = (obj) => {
      if (!obj) return
      const countBefore = list.length
      if (Array.isArray(obj.geopolygons)) obj.geopolygons.forEach(addGeometry)
      if (Array.isArray(obj.boundaries)) obj.boundaries.forEach(addGeometry)
      if (Array.isArray(obj.zipCodeBoundaries)) obj.zipCodeBoundaries.forEach(addGeometry)
      if (Array.isArray(obj.regions)) obj.regions.forEach(addGeometry)
      if (Array.isArray(obj.shapes)) obj.shapes.forEach(addGeometry)
      if (Array.isArray(obj.savedMarkers)) obj.savedMarkers.forEach(addGeometry)
      if (Array.isArray(obj.markers)) obj.markers.forEach(addGeometry)
      if (list.length === countBefore) addGeometry(obj)
    }

    const sources = []

    // 1. If selectionStore has multiple selected menu items, exclusively show those
    if (selectionStore?.selectedMenuItems && selectionStore.selectedMenuItems.length > 0) {
      sources.push(() => selectionStore.selectedMenuItems.forEach(processItem))
    }

    // 2. Editable shape currently in creation or edit mode (e.g. Isochrone)
    if (selectionStore?.editableShape) {
      sources.push(() => processItem(selectionStore.editableShape))
    }

    // 3. SelectionStore item (if individual shape or item selected)
    if (selectionStore?.item) {
      sources.push(() => processItem(selectionStore.item))
    }

    // 4. Prop item (passed from dropdown)
    if (item) {
      sources.push(() => processItem(item))
    }

    // 5. Explicit single polygon in right sidebar (prefer shape model, fallback to raw)
    if (rightSidebarStore?.singlePolygonData) {
      const raw = rightSidebarStore.singlePolygonData
      let model = null
      try {
        model = typeof rightSidebarStore.findTargetGroupShape === 'function'
          ? rightSidebarStore.findTargetGroupShape(raw)
          : null
      } catch (e) {}
      if (model) sources.push(() => processItem(model))
      sources.push(() => processItem(raw))
    }

    // 6. Target Group from RightSidebarStore (when whole group is active without individual shape)
    if (rightSidebarStore?.targetGroupId && dataStore?.targetGroups) {
      const foundGroup = dataStore.targetGroups.find(
        g => g && String(g.pk) === String(rightSidebarStore.targetGroupId)
      )
      if (foundGroup) {
        sources.push(() => processItem(foundGroup))
      }
    }

    // 7. POI temporary markers from dataStore.poi
    if (dataStore?.poi && Array.isArray(dataStore.poi.temporaryMarkers) && dataStore.poi.temporaryMarkers.length > 0) {
      sources.push(() => dataStore.poi.temporaryMarkers.forEach(addGeometry))
    }

    // 8. Fallback: polygon data list from rightSidebarStore
    if (rightSidebarStore && Array.isArray(rightSidebarStore.polygonData)) {
      sources.push(() => rightSidebarStore.polygonData.forEach(processItem))
    }

    for (const collect of sources) {
      collect()
      if (list.length > 0) break
    }

    return list
  }, [
    item,
    selectionStore?.item,
    selectionStore?.editableShape,
    selectionStore?.selectedMenuItems,
    selectionStore?.selectedColor,
    selectionStore?.selectedTransparency,
    selectionStore?.colorEditItem,
    rightSidebarStore?.targetGroupId,
    rightSidebarStore?.singlePolygonData,
    rightSidebarStore?.polygonData,
    dataStore?.targetGroups,
    dataStore?.markerColors,
    dataStore?.shapeStatuses,
    dataStore?.poi?.temporaryMarkers
  ])

  const targetName = useMemo(() => {
    if (rightSidebarStore && rightSidebarStore.singlePolygonData) {
      const name = resolveItemName(rightSidebarStore.singlePolygonData)
      if (name) return name
    }
    if (item) {
      const name = resolveItemName(item)
      if (name) return name
    }
    if (selectionStore && selectionStore.item) {
      const name = resolveItemName(selectionStore.item)
      if (name) return name
    }
    if (rightSidebarStore && rightSidebarStore.targetGroupName && rightSidebarStore.targetGroupName.trim()) {
      return rightSidebarStore.targetGroupName.trim()
    }
    return 'N/A'
  }, [item, selectionStore?.item, rightSidebarStore?.singlePolygonData, rightSidebarStore?.targetGroupName])

  const getNumericValue = (val) => {
    if (val == null) return 0
    if (typeof val === 'number') return isNaN(val) ? 0 : val
    const parsed = parseFloat(String(val).replace(/[^0-9.-]/g, ''))
    return isNaN(parsed) ? 0 : parsed
  }

  const getEntryPopulation = (entry) => {
    if (entry == null) return 0
    if (typeof entry === 'number') return isNaN(entry) ? 0 : entry
    if (typeof entry === 'string') return getNumericValue(entry)
    if (entry.sum != null && getNumericValue(entry.sum) > 0) return getNumericValue(entry.sum)
    if (entry.sum_pop != null && getNumericValue(entry.sum_pop) > 0) return getNumericValue(entry.sum_pop)
    if (entry.pop_tot != null && getNumericValue(entry.pop_tot) > 0) return getNumericValue(entry.pop_tot)
    if (entry.total_population != null && getNumericValue(entry.total_population) > 0) return getNumericValue(entry.total_population)
    if (entry.population != null && getNumericValue(entry.population) > 0) return getNumericValue(entry.population)
    if (entry.value != null && getNumericValue(entry.value) > 0) return getNumericValue(entry.value)
    return 0
  }

  const isLandscanEntry = (entry) => {
    if (!entry || typeof entry !== 'object') return false
    const ds = (entry.type || entry.ds_name || entry.dataset || entry.source || '').toString().toLowerCase()
    return ds.includes('landscan') || ds.includes('daytime') || (!ds && !ds.includes('worldpop') && !ds.includes('residential'))
  }

  const extractLandscan = (obj, targetYear) => {
    if (!obj) return 0

    // 1. Direct stats.total_population array
    if (obj.stats && Array.isArray(obj.stats.total_population) && obj.stats.total_population.length > 0) {
      const popArr = obj.stats.total_population
      const lsItems = popArr.filter(isLandscanEntry)
      const listToUse = lsItems.length > 0 ? lsItems : popArr
      if (targetYear) {
        const match = listToUse.find(p => String(p.year) === String(targetYear))
        const val = getEntryPopulation(match)
        if (val > 0) return val
      }
      for (const y of ['2024', '2026', '2021', '2022', '2023', '2025']) {
        const match = listToUse.find(p => String(p.year) === y)
        const val = getEntryPopulation(match)
        if (val > 0) return val
      }
      for (let i = listToUse.length - 1; i >= 0; i--) {
        const val = getEntryPopulation(listToUse[i])
        if (val > 0) return val
      }
    }

    // 2. Direct stats properties
    if (obj.stats) {
      if (obj.stats.population != null && getNumericValue(obj.stats.population) > 0) return getNumericValue(obj.stats.population)
      if (obj.stats.total_population != null && getNumericValue(obj.stats.total_population) > 0) return getNumericValue(obj.stats.total_population)
      if (obj.stats.daytime_population != null && getNumericValue(obj.stats.daytime_population) > 0) return getNumericValue(obj.stats.daytime_population)
      if (obj.stats.sum_pop != null && getNumericValue(obj.stats.sum_pop) > 0) return getNumericValue(obj.stats.sum_pop)
      if (obj.stats.sum != null && getNumericValue(obj.stats.sum) > 0) return getNumericValue(obj.stats.sum)
    }

    // 3. population_datas (object or array)
    if (obj.population_datas) {
      const pDatas = obj.population_datas
      if (typeof pDatas === 'object' && !Array.isArray(pDatas)) {
        const yearKeys = Object.keys(pDatas).sort()
        const keysToTry = [
          ...(targetYear ? [String(targetYear)] : []),
          '2024', '2026', '2021', '2022', '2023', '2025',
          ...yearKeys.slice().reverse()
        ]
        const tried = new Set()
        for (const k of keysToTry) {
          if (!k || tried.has(k) || !pDatas[k]) continue
          tried.add(k)
          const bucket = pDatas[k]
          if (Array.isArray(bucket)) {
            const ls = bucket.find(isLandscanEntry) || bucket[0]
            const val = getEntryPopulation(ls)
            if (val > 0) return val
            for (const item of bucket) {
              const v = getEntryPopulation(item)
              if (v > 0) return v
            }
          } else {
            const val = getEntryPopulation(bucket)
            if (val > 0) return val
          }
        }
      } else if (Array.isArray(pDatas) && pDatas.length > 0) {
        const lsItems = pDatas.filter(isLandscanEntry)
        const listToUse = lsItems.length > 0 ? lsItems : pDatas
        for (const item of listToUse) {
          const val = getEntryPopulation(item)
          if (val > 0) return val
        }
      }
    }

    // 4. trends array
    if (Array.isArray(obj.trends) && obj.trends.length > 0) {
      if (targetYear) {
        const match = obj.trends.find(t => String(t.year) === String(targetYear))
        const val = getEntryPopulation(match)
        if (val > 0) return val
      }
      for (const y of ['2024', '2026', '2021', '2022', '2023', '2025']) {
        const match = obj.trends.find(t => String(t.year) === y)
        const val = getEntryPopulation(match)
        if (val > 0) return val
      }
      for (let i = obj.trends.length - 1; i >= 0; i--) {
        const val = getEntryPopulation(obj.trends[i])
        if (val > 0) return val
      }
    }

    // 5. analysis (RegionAnalysis)
    if (obj.analysis) {
      if (obj.analysis.sum != null && getNumericValue(obj.analysis.sum) > 0) return getNumericValue(obj.analysis.sum)
      if (obj.analysis.sum_pop != null && getNumericValue(obj.analysis.sum_pop) > 0) return getNumericValue(obj.analysis.sum_pop)
      if (obj.analysis.population != null && getNumericValue(obj.analysis.population) > 0) return getNumericValue(obj.analysis.population)
      if (obj.analysis.total_population != null && getNumericValue(obj.analysis.total_population) > 0) return getNumericValue(obj.analysis.total_population)
    }

    // 6. Direct properties on obj
    if (obj.population != null && getNumericValue(obj.population) > 0) return getNumericValue(obj.population)
    if (obj.total_population != null && getNumericValue(obj.total_population) > 0) return getNumericValue(obj.total_population)
    if (obj.sum != null && getNumericValue(obj.sum) > 0) return getNumericValue(obj.sum)
    if (obj.sum_pop != null && getNumericValue(obj.sum_pop) > 0) return getNumericValue(obj.sum_pop)

    // 7. GeoJSON / properties
    if (obj.properties) {
      const p = obj.properties
      if (p.population != null && getNumericValue(p.population) > 0) return getNumericValue(p.population)
      if (p.total_population != null && getNumericValue(p.total_population) > 0) return getNumericValue(p.total_population)
      if (p.sum != null && getNumericValue(p.sum) > 0) return getNumericValue(p.sum)
      if (p.sum_pop != null && getNumericValue(p.sum_pop) > 0) return getNumericValue(p.sum_pop)
      if (p.pop != null && getNumericValue(p.pop) > 0) return getNumericValue(p.pop)
      if (p.DAYTIME_POP != null && getNumericValue(p.DAYTIME_POP) > 0) return getNumericValue(p.DAYTIME_POP)
    }

    // 8. Child collections (geopolygons, boundaries, etc.)
    if (Array.isArray(obj.geopolygons) && obj.geopolygons.length > 0) {
      let sum = 0
      obj.geopolygons.forEach(g => { sum += extractLandscan(g, targetYear) })
      if (sum > 0) return sum
    }
    if (Array.isArray(obj.boundaries) && obj.boundaries.length > 0) {
      let sum = 0
      obj.boundaries.forEach(b => { sum += extractLandscan(b, targetYear) })
      if (sum > 0) return sum
    }
    if (Array.isArray(obj.zipCodeBoundaries) && obj.zipCodeBoundaries.length > 0) {
      let sum = 0
      obj.zipCodeBoundaries.forEach(z => { sum += extractLandscan(z, targetYear) })
      if (sum > 0) return sum
    }
    if (Array.isArray(obj.shapes) && obj.shapes.length > 0) {
      let sum = 0
      obj.shapes.forEach(s => { sum += extractLandscan(s, targetYear) })
      if (sum > 0) return sum
    }

    return 0
  }

  const extractIncome = (obj) => {
    if (!obj) return 0

    // 1. stats.hh_income (array)
    if (obj.stats && Array.isArray(obj.stats.hh_income) && obj.stats.hh_income.length > 0) {
      const valid = obj.stats.hh_income.filter(x => x && (getNumericValue(x.value) > 0 || getNumericValue(x.income) > 0 || getNumericValue(x.hh_income) > 0))
      if (valid.length > 0) {
        const y2021 = valid.find(x => String(x.year) === '2021' || x.year === 2021)
        if (y2021) return getNumericValue(y2021.value) || getNumericValue(y2021.income) || getNumericValue(y2021.hh_income)
        const y2024 = valid.find(x => String(x.year) === '2024' || x.year === 2024)
        if (y2024) return getNumericValue(y2024.value) || getNumericValue(y2024.income) || getNumericValue(y2024.hh_income)
        const last = valid[valid.length - 1]
        return getNumericValue(last.value) || getNumericValue(last.income) || getNumericValue(last.hh_income)
      }
    }

    // 2. stats direct income properties
    if (obj.stats) {
      if (obj.stats.median_hh_income != null && getNumericValue(obj.stats.median_hh_income) > 0) return getNumericValue(obj.stats.median_hh_income)
      if (obj.stats.median_income != null && getNumericValue(obj.stats.median_income) > 0) return getNumericValue(obj.stats.median_income)
      if (obj.stats.hh_income != null && getNumericValue(obj.stats.hh_income) > 0) return getNumericValue(obj.stats.hh_income)
      if (obj.stats.income != null && getNumericValue(obj.stats.income) > 0) return getNumericValue(obj.stats.income)
      if (obj.stats.average_income != null && getNumericValue(obj.stats.average_income) > 0) return getNumericValue(obj.stats.average_income)
    }

    // 3. incomeData array
    if (Array.isArray(obj.incomeData) && obj.incomeData.length > 0) {
      const valid = obj.incomeData.filter(x => x && (getNumericValue(x.income) > 0 || getNumericValue(x.hh_income) > 0 || getNumericValue(x.value) > 0))
      if (valid.length > 0) {
        const y2021 = valid.find(x => String(x.year) === '2021' || x.year === 2021)
        if (y2021) return getNumericValue(y2021.income) || getNumericValue(y2021.hh_income) || getNumericValue(y2021.value)
        const last = valid[valid.length - 1]
        return getNumericValue(last.income) || getNumericValue(last.hh_income) || getNumericValue(last.value)
      }
    }

    // 4. population_datas
    if (obj.population_datas) {
      const pDatas = obj.population_datas
      if (typeof pDatas === 'object' && !Array.isArray(pDatas)) {
        const yearKeys = Object.keys(pDatas).sort()
        const keysToTry = ['2021', '2024', 2021, 2024, ...yearKeys.slice().reverse()]
        const tried = new Set()
        for (const y of keysToTry) {
          if (!y || tried.has(String(y)) || !pDatas[String(y)]) continue
          tried.add(String(y))
          const bucket = pDatas[String(y)]
          const arr = Array.isArray(bucket) ? bucket : [bucket]
          for (const d of arr) {
            if (!d) continue
            const v = getNumericValue(d.hh_income) || getNumericValue(d.income) || getNumericValue(d.total_income) || getNumericValue(d.average_income) || getNumericValue(d.median_income)
            if (v > 0) return v
          }
        }
      } else if (Array.isArray(pDatas) && pDatas.length > 0) {
        for (const d of pDatas) {
          if (!d) continue
          const v = getNumericValue(d.hh_income) || getNumericValue(d.income) || getNumericValue(d.total_income) || getNumericValue(d.average_income)
          if (v > 0) return v
        }
      }
    }

    // 5. analysis (RegionAnalysis)
    if (obj.analysis) {
      if (obj.analysis.hh_income != null && getNumericValue(obj.analysis.hh_income) > 0) return getNumericValue(obj.analysis.hh_income)
      if (obj.analysis.median_income != null && getNumericValue(obj.analysis.median_income) > 0) return getNumericValue(obj.analysis.median_income)
      if (obj.analysis.income != null && getNumericValue(obj.analysis.income) > 0) return getNumericValue(obj.analysis.income)
      if (obj.analysis.average_income != null && getNumericValue(obj.analysis.average_income) > 0) return getNumericValue(obj.analysis.average_income)
    }

    // 6. Direct properties
    if (obj.median_hh_income != null && getNumericValue(obj.median_hh_income) > 0) return getNumericValue(obj.median_hh_income)
    if (obj.hh_income != null && getNumericValue(obj.hh_income) > 0) return getNumericValue(obj.hh_income)
    if (obj.income != null && getNumericValue(obj.income) > 0) return getNumericValue(obj.income)
    if (obj.median_income != null && getNumericValue(obj.median_income) > 0) return getNumericValue(obj.median_income)

    // 7. properties (GeoJSON)
    if (obj.properties) {
      const p = obj.properties
      if (p.hh_income != null && getNumericValue(p.hh_income) > 0) return getNumericValue(p.hh_income)
      if (p.median_hh_income != null && getNumericValue(p.median_hh_income) > 0) return getNumericValue(p.median_hh_income)
      if (p.median_income != null && getNumericValue(p.median_income) > 0) return getNumericValue(p.median_income)
      if (p.income != null && getNumericValue(p.income) > 0) return getNumericValue(p.income)
      if (p.MEDHHINC != null && getNumericValue(p.MEDHHINC) > 0) return getNumericValue(p.MEDHHINC)
    }

    // 8. Child collections
    if (Array.isArray(obj.geopolygons) && obj.geopolygons.length > 0) {
      const valid = obj.geopolygons.map(extractIncome).filter(v => v > 0)
      if (valid.length > 0) return Math.round(valid.reduce((a, b) => a + b, 0) / valid.length)
    }
    if (Array.isArray(obj.boundaries) && obj.boundaries.length > 0) {
      const valid = obj.boundaries.map(extractIncome).filter(v => v > 0)
      if (valid.length > 0) return Math.round(valid.reduce((a, b) => a + b, 0) / valid.length)
    }
    if (Array.isArray(obj.shapes) && obj.shapes.length > 0) {
      const valid = obj.shapes.map(extractIncome).filter(v => v > 0)
      if (valid.length > 0) return Math.round(valid.reduce((a, b) => a + b, 0) / valid.length)
    }

    return 0
  }

  const population = useMemo(() => {
    const targetYear = (rightSidebarStore && (rightSidebarStore.populationSelectedYear || rightSidebarStore.latestYear)) || '2024'

    // 1. RightSidebarStore total population
    if (rightSidebarStore && rightSidebarStore.totalPopulation != null && getNumericValue(rightSidebarStore.totalPopulation) > 0) {
      return getNumericValue(rightSidebarStore.totalPopulation)
    }

    // 2. RightSidebarStore single polygon
    if (rightSidebarStore && rightSidebarStore.singlePolygonData) {
      const val = extractLandscan(rightSidebarStore.singlePolygonData, targetYear)
      if (val > 0) return val
    }
    if (rightSidebarStore && Array.isArray(rightSidebarStore.rawSinglePolygonData) && rightSidebarStore.rawSinglePolygonData.length > 0) {
      const val = extractLandscan(rightSidebarStore.rawSinglePolygonData[0], targetYear)
      if (val > 0) return val
    }

    // 3. Item prop
    if (item) {
      const val = extractLandscan(item, targetYear)
      if (val > 0) return val
    }

    // 4. SelectionStore item
    if (selectionStore && selectionStore.item) {
      const val = extractLandscan(selectionStore.item, targetYear)
      if (val > 0) return val
    }

    // 5. Multi-selection items
    if (selectionStore && Array.isArray(selectionStore.selectedMenuItems) && selectionStore.selectedMenuItems.length > 0) {
      let sum = 0
      selectionStore.selectedMenuItems.forEach(m => { sum += extractLandscan(m, targetYear) })
      if (sum > 0) return sum
    }

    // 6. Polygons in right sidebar
    const polyList = (rightSidebarStore && Array.isArray(rightSidebarStore.rawPolygonData) && rightSidebarStore.rawPolygonData.length > 0)
      ? rightSidebarStore.rawPolygonData
      : (rightSidebarStore && Array.isArray(rightSidebarStore.polygonData) ? rightSidebarStore.polygonData : [])

    if (polyList.length > 0) {
      let sum = 0
      polyList.forEach(p => { sum += extractLandscan(p, targetYear) })
      if (sum > 0) return sum
    }

    // 7. incomeAndPopDataForChart
    if (rightSidebarStore && Array.isArray(rightSidebarStore.incomeAndPopDataForChart) && rightSidebarStore.incomeAndPopDataForChart.length > 0) {
      let sum = 0
      rightSidebarStore.incomeAndPopDataForChart.forEach(d => {
        if (d && getNumericValue(d.population) > 0) sum += getNumericValue(d.population)
      })
      if (sum > 0) return sum
    }

    // 8. populationData.data & dissolved_data
    if (rightSidebarStore && rightSidebarStore.populationData) {
      const popData = rightSidebarStore.populationData
      if (Array.isArray(popData.data) && popData.data.length > 0) {
        const match = popData.data.find(d => targetYear && String(d.year) === String(targetYear) && isLandscanEntry(d)) ||
          popData.data.find(d => isLandscanEntry(d)) ||
          popData.data[popData.data.length - 1]
        if (match) {
          const val = getEntryPopulation(match)
          if (val > 0) return val
        }
      }
      if (Array.isArray(popData.dissolved_data) && popData.dissolved_data.length > 0) {
        const match = popData.dissolved_data.find(d => targetYear && String(d.year) === String(targetYear)) ||
          popData.dissolved_data[popData.dissolved_data.length - 1]
        if (match) {
          const val = getEntryPopulation(match)
          if (val > 0) return val
        }
      }
    }

    // 9. populationTrends
    if (rightSidebarStore && Array.isArray(rightSidebarStore.populationTrends) && rightSidebarStore.populationTrends.length > 0) {
      const match = rightSidebarStore.populationTrends.find(t => targetYear && String(t.year) === String(targetYear)) ||
        rightSidebarStore.populationTrends[rightSidebarStore.populationTrends.length - 1]
      if (match) {
        const val = getEntryPopulation(match)
        if (val > 0) return val
      }
    }

    return 0
  }, [
    rightSidebarStore?.singlePolygonData,
    rightSidebarStore?.rawSinglePolygonData,
    rightSidebarStore?.polygonData,
    rightSidebarStore?.rawPolygonData,
    rightSidebarStore?.populationData,
    rightSidebarStore?.incomeAndPopDataForChart,
    rightSidebarStore?.populationTrends,
    rightSidebarStore?.totalPopulation,
    rightSidebarStore?.populationSelectedYear,
    rightSidebarStore?.latestYear,
    item,
    selectionStore?.item,
    selectionStore?.selectedMenuItems
  ])

  const income = useMemo(() => {
    // 1. RightSidebarStore medianHouseholdIncome computed getter
    if (rightSidebarStore) {
      if (rightSidebarStore.medianHouseholdIncome !== undefined && rightSidebarStore.medianHouseholdIncome !== null) {
        const val = getNumericValue(rightSidebarStore.medianHouseholdIncome)
        if (val > 0) return val
      }
      if (rightSidebarStore.medianHHIncome != null) {
        const val = getNumericValue(rightSidebarStore.medianHHIncome)
        if (val > 0) return val
      }
      if (rightSidebarStore.averageIncome != null) {
        const val = getNumericValue(rightSidebarStore.averageIncome)
        if (val > 0) return val
      }
    }

    // 2. RightSidebarStore single polygon
    if (rightSidebarStore && rightSidebarStore.singlePolygonData) {
      const val = extractIncome(rightSidebarStore.singlePolygonData)
      if (val > 0) return val
    }
    if (rightSidebarStore && Array.isArray(rightSidebarStore.rawSinglePolygonData) && rightSidebarStore.rawSinglePolygonData.length > 0) {
      const val = extractIncome(rightSidebarStore.rawSinglePolygonData[0])
      if (val > 0) return val
    }

    // 3. Item prop
    if (item) {
      const val = extractIncome(item)
      if (val > 0) return val
    }

    // 4. SelectionStore item
    if (selectionStore && selectionStore.item) {
      const val = extractIncome(selectionStore.item)
      if (val > 0) return val
    }

    // 5. Multi-selection items
    if (selectionStore && Array.isArray(selectionStore.selectedMenuItems) && selectionStore.selectedMenuItems.length > 0) {
      const valid = selectionStore.selectedMenuItems.map(extractIncome).filter(v => v > 0)
      if (valid.length > 0) return Math.round(valid.reduce((a, b) => a + b, 0) / valid.length)
    }

    // 6. Polygons in right sidebar
    const polyList = (rightSidebarStore && Array.isArray(rightSidebarStore.rawPolygonData) && rightSidebarStore.rawPolygonData.length > 0)
      ? rightSidebarStore.rawPolygonData
      : (rightSidebarStore && Array.isArray(rightSidebarStore.polygonData) ? rightSidebarStore.polygonData : [])

    if (polyList.length > 0) {
      const valid = polyList.map(extractIncome).filter(v => v > 0)
      if (valid.length > 0) return Math.round(valid.reduce((a, b) => a + b, 0) / valid.length)
    }

    // 7. incomeAndPopDataForChart
    if (rightSidebarStore && Array.isArray(rightSidebarStore.incomeAndPopDataForChart) && rightSidebarStore.incomeAndPopDataForChart.length > 0) {
      const valid = rightSidebarStore.incomeAndPopDataForChart.map(d => getNumericValue(d.income)).filter(v => v > 0)
      if (valid.length > 0) return Math.round(valid.reduce((a, b) => a + b, 0) / valid.length)
    }

    // 8. incomeProgressData
    if (rightSidebarStore && Array.isArray(rightSidebarStore.incomeProgressData) && rightSidebarStore.incomeProgressData.length > 0) {
      const valid = rightSidebarStore.incomeProgressData.map(d => getNumericValue(d.income)).filter(v => v > 0)
      if (valid.length > 0) return Math.round(valid.reduce((a, b) => a + b, 0) / valid.length)
    }

    // 9. populationData (API)
    if (rightSidebarStore && rightSidebarStore.populationData) {
      const popData = rightSidebarStore.populationData
      if (Array.isArray(popData.data) && popData.data.length > 0) {
        const y2021 = popData.data.find(d => (String(d.year) === '2021' || d.year === 2021) && (getNumericValue(d.total_income) > 0 || getNumericValue(d.average_income) > 0))
        if (y2021) {
          const val = getNumericValue(y2021.average_income) || getNumericValue(y2021.total_income)
          if (val > 0) return val
        }
        const available = popData.data.filter(d => getNumericValue(d.total_income) > 0 || getNumericValue(d.average_income) > 0)
        if (available.length > 0) {
          const latest = available[available.length - 1]
          const val = getNumericValue(latest.average_income) || getNumericValue(latest.total_income)
          if (val > 0) return val
        }
      }
      if (Array.isArray(popData.dissolved_data) && popData.dissolved_data.length > 0) {
        const y2021 = popData.dissolved_data.find(d => (String(d.year) === '2021' || d.year === 2021) && (getNumericValue(d.total_income) > 0 || getNumericValue(d.average_income) > 0))
        if (y2021) {
          const val = getNumericValue(y2021.average_income) || getNumericValue(y2021.total_income)
          if (val > 0) return val
        }
        const available = popData.dissolved_data.filter(d => getNumericValue(d.total_income) > 0 || getNumericValue(d.average_income) > 0)
        if (available.length > 0) {
          const latest = available[available.length - 1]
          const val = getNumericValue(latest.average_income) || getNumericValue(latest.total_income)
          if (val > 0) return val
        }
      }
    }

    return 0
  }, [
    rightSidebarStore?.singlePolygonData,
    rightSidebarStore?.rawSinglePolygonData,
    rightSidebarStore?.polygonData,
    rightSidebarStore?.rawPolygonData,
    rightSidebarStore?.medianHouseholdIncome,
    rightSidebarStore?.medianHHIncome,
    rightSidebarStore?.averageIncome,
    rightSidebarStore?.incomeAndPopDataForChart,
    rightSidebarStore?.incomeProgressData,
    rightSidebarStore?.populationData,
    item,
    selectionStore?.item,
    selectionStore?.selectedMenuItems
  ])

  // Fit bounds to geometries on open
  useEffect(() => {
    if (!isOpen || !geometries.length) return

    let calculatedBounds = null
    geometries.forEach((g) => {
      if (g.geoJSON) {
        try {
          const b = geoJSONBounds(g.geoJSON)
          if (b && b[0] && b[1] && !isNaN(b[0][0]) && !isNaN(b[1][0])) {
            if (!calculatedBounds) {
              calculatedBounds = [[b[0][0], b[0][1]], [b[1][0], b[1][1]]]
            } else {
              calculatedBounds[0][0] = Math.min(calculatedBounds[0][0], b[0][0])
              calculatedBounds[0][1] = Math.min(calculatedBounds[0][1], b[0][1])
              calculatedBounds[1][0] = Math.max(calculatedBounds[1][0], b[1][0])
              calculatedBounds[1][1] = Math.max(calculatedBounds[1][1], b[1][1])
            }
          }
        } catch (err) {}
      }
    })

    if (calculatedBounds) {
      const centerLat = (calculatedBounds[0][0] + calculatedBounds[1][0]) / 2
      const centerLng = (calculatedBounds[0][1] + calculatedBounds[1][1]) / 2
      const latDiff = Math.abs(calculatedBounds[1][0] - calculatedBounds[0][0])
      const lngDiff = Math.abs(calculatedBounds[1][1] - calculatedBounds[0][1])
      const maxDiff = Math.max(latDiff, lngDiff)

      let calcZoom = 12
      if (maxDiff > 20) calcZoom = 4
      else if (maxDiff > 10) calcZoom = 5
      else if (maxDiff > 5) calcZoom = 6
      else if (maxDiff > 2) calcZoom = 8
      else if (maxDiff > 1) calcZoom = 9
      else if (maxDiff > 0.5) calcZoom = 10
      else if (maxDiff > 0.2) calcZoom = 11
      else if (maxDiff > 0.08) calcZoom = 12
      else calcZoom = 13

      setViewport({
        center: [centerLat, centerLng],
        zoom: calcZoom
      })
    }
  }, [isOpen, geometries])

  const loadImageAsync = async (url) => {
    if (!url) return null

    const server = (constants.SERVER || '').replace(/\/+$/, '')

    // 1. Try proxying through backend endpoint to guarantee 100% origin-clean canvas
    if (url.startsWith('http://') || url.startsWith('https://')) {
      try {
        const proxyUrl = `${server}/api/geodata/wms-proxy/?url=${encodeURIComponent(url)}`
        const proxyRes = await fetch(proxyUrl)
        if (proxyRes.ok) {
          const blob = await proxyRes.blob()
          if (blob && (blob.type.startsWith('image/') || blob.size > 0)) {
            const objectUrl = URL.createObjectURL(blob)
            return await new Promise((resolve) => {
              const img = new Image()
              img.onload = () => {
                setTimeout(() => {
                  try {
                    URL.revokeObjectURL(objectUrl)
                  } catch (e) {}
                }, 60000)
                resolve(img)
              }
              img.onerror = () => {
                try {
                  URL.revokeObjectURL(objectUrl)
                } catch (e) {}
                resolve(null)
              }
              img.src = objectUrl
            })
          }
        }
      } catch (e) {
        // Fallback
      }
    }

    // 2. Direct CORS fetch
    try {
      const response = await fetch(url, { mode: 'cors' })
      if (response.ok) {
        const blob = await response.blob()
        if (blob && (blob.type.startsWith('image/') || blob.size > 0)) {
          const objectUrl = URL.createObjectURL(blob)
          return await new Promise((resolve) => {
            const img = new Image()
            img.onload = () => {
              setTimeout(() => {
                try {
                  URL.revokeObjectURL(objectUrl)
                } catch (e) {}
              }, 60000)
              resolve(img)
            }
            img.onerror = () => {
              try {
                URL.revokeObjectURL(objectUrl)
              } catch (e) {}
              resolve(null)
            }
            img.src = objectUrl
          })
        }
      }
    } catch (e) {
      // Fallback
    }

    return null
  }

  const latLngToTileXY = (lat, lng, zoom) => {
    const n = Math.pow(2, zoom)
    const x = Math.floor(((lng + 180) / 360) * n)
    const latRad = (lat * Math.PI) / 180
    const y = Math.floor(
      ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n
    )
    return { x, y }
  }

  const tileXYToLatLng = (x, y, zoom) => {
    const n = Math.pow(2, zoom)
    const lng = (x / n) * 360 - 180
    const latRad = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n)))
    const lat = (latRad * 180) / Math.PI
    return { lat, lng }
  }

  const generateImageBlob = async () => {
    const mapInstance = mapRef.current?.leafletElement
    if (!mapInstance) return null

    const width = 1920
    const height = 1080
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')

    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'

    ctx.fillStyle = '#E5E7EB'
    ctx.fillRect(0, 0, width, height)

    const mapContainer = mapInstance.getContainer()
    const containerRect = mapContainer ? mapContainer.getBoundingClientRect() : { width: 960, height: 540 }
    const scaleX = width / (containerRect.width || 960)
    const scaleY = height / (containerRect.height || 540)

    const zoom = Math.round(mapInstance.getZoom())
    const bounds = mapInstance.getBounds()

    try {
      const north = bounds.getNorth()
      const south = bounds.getSouth()
      const west = bounds.getWest()
      const east = bounds.getEast()

      const tlTile = latLngToTileXY(north, west, zoom)
      const brTile = latLngToTileXY(south, east, zoom)

      const minX = Math.min(tlTile.x, brTile.x) - 1
      const maxX = Math.max(tlTile.x, brTile.x) + 1
      const minY = Math.min(tlTile.y, brTile.y) - 1
      const maxY = Math.max(tlTile.y, brTile.y) + 1

      const tileList = []
      for (let x = minX; x <= maxX; x++) {
        for (let y = minY; y <= maxY; y++) {
          const pTopLeft = mapInstance.latLngToContainerPoint(tileXYToLatLng(x, y, zoom))
          const pBottomRight = mapInstance.latLngToContainerPoint(tileXYToLatLng(x + 1, y + 1, zoom))
          const dx = pTopLeft.x * scaleX
          const dy = pTopLeft.y * scaleY
          const dWidth = (pBottomRight.x - pTopLeft.x) * scaleX
          const dHeight = (pBottomRight.y - pTopLeft.y) * scaleY

          tileList.push({ x, y, z: zoom, dx, dy, dWidth, dHeight })
        }
      }

      const getTileUrls = (x, y, z) => [
        `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`,
        `https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/${z}/${y}/${x}`
      ]

      const tilePromises = tileList.map(async (tile) => {
        const urls = getTileUrls(tile.x, tile.y, tile.z)
        const imgs = await Promise.all(urls.map(url => loadImageAsync(url)))
        return { ...tile, imgs }
      })

      const loadedTiles = await Promise.all(tilePromises)

      // 1. Draw Base Map tiles
      loadedTiles.forEach((tile) => {
        tile.imgs.forEach((img) => {
          if (img) {
            ctx.drawImage(img, tile.dx, tile.dy, tile.dWidth, tile.dHeight)
          }
        })
      })

      // 2. Draw Heatmap Overlay if active
      const isHeatmapActive = Boolean(uiStore && uiStore.densityLayer && uiStore.densityLayer !== 'none')
      if (isHeatmapActive) {
        try {
          const heatmapConfig = resolveHeatmapConfig(uiStore)
          const heatTilePromises = tileList.map(async (tile) => {
            const tileUrl = getHeatmapTileUrl(tile.x, tile.y, tile.z, heatmapConfig)
            const img = await loadImageAsync(tileUrl)
            return { ...tile, img }
          })

          const loadedHeatTiles = await Promise.all(heatTilePromises)
          const hasLoadedTiles = loadedHeatTiles.some(t => Boolean(t.img))

          if (hasLoadedTiles) {
            ctx.save()
            ctx.globalAlpha = 0.75
            loadedHeatTiles.forEach((tile) => {
              if (tile.img) {
                ctx.drawImage(tile.img, tile.dx, tile.dy, tile.dWidth, tile.dHeight)
              }
            })
            ctx.restore()
          } else {
            const viewportWmsUrl = getHeatmapViewportUrl(bounds, width, height, heatmapConfig)
            const viewportHeatImg = await loadImageAsync(viewportWmsUrl)
            if (viewportHeatImg) {
              ctx.save()
              ctx.globalAlpha = 0.75
              ctx.drawImage(viewportHeatImg, 0, 0, width, height)
              ctx.restore()
            }
          }
        } catch (e) {
          console.warn('Heatmap draw error:', e)
        }
      }
    } catch (e) {
      console.warn('Tile render error:', e)
    }

    // Draw GeoJSON shapes
    geometries.forEach((g) => {
      if (g.geoJSON) {
        const strokeColor = (g.hasBorder && g.borderColor) ? g.borderColor : (g.borderColor || g.color)
        const strokeWidth = (g.hasBorder && g.borderWidth != null) ? g.borderWidth : (g.borderWidth || 3.5)
        const dashArray = (g.hasBorder && g.borderDashArray) ? g.borderDashArray : g.borderDashArray
        const strokeOpacity = g.borderOpacity != null ? g.borderOpacity : 1

        drawGeoJSONOnCanvas(
          ctx,
          g.geoJSON,
          mapInstance,
          scaleX,
          scaleY,
          g.color,
          strokeColor,
          g.transparency,
          strokeWidth,
          g.name || g.label,
          dashArray,
          strokeOpacity
        )
      }
    })

    // Watermark Pill (25% larger)
    const pillX = 40
    const pillY = height - 90
    const pillWidth = 325
    const pillHeight = 55
    const pillRadius = 28

    ctx.save()
    ctx.fillStyle = 'rgba(17, 24, 39, 0.88)'
    ctx.shadowColor = 'rgba(0, 0, 0, 0.25)'
    ctx.shadowBlur = 14
    ctx.shadowOffsetY = 5
    ctx.beginPath()
    if (ctx.roundRect) {
      ctx.roundRect(pillX, pillY, pillWidth, pillHeight, pillRadius)
    } else {
      ctx.rect(pillX, pillY, pillWidth, pillHeight)
    }
    ctx.fill()
    ctx.restore()

    ctx.save()
    ctx.font = '500 21px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    ctx.fillStyle = '#FFFFFF'
    ctx.textBaseline = 'middle'
    ctx.textAlign = 'center'
    ctx.fillText('populationexplorer.com', pillX + pillWidth / 2, pillY + pillHeight / 2)
    ctx.restore()

    // Metric Card (25% larger)
    const hasIncome = income != null && !isNaN(income) && Number(income) > 0
    const cardWidth = 525
    const cardHeight = hasIncome ? 180 : 135
    const cardX = width - cardWidth - 40
    const cardY = height - cardHeight - 40
    const cardRadius = 20

    ctx.save()
    ctx.shadowColor = 'rgba(0, 0, 0, 0.2)'
    ctx.shadowBlur = 28
    ctx.shadowOffsetY = 10
    ctx.fillStyle = '#FFFFFF'
    ctx.beginPath()
    if (ctx.roundRect) {
      ctx.roundRect(cardX, cardY, cardWidth, cardHeight, cardRadius)
    } else {
      ctx.rect(cardX, cardY, cardWidth, cardHeight)
    }
    ctx.fill()
    ctx.restore()

    ctx.save()
    ctx.font = '600 25px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    ctx.fillStyle = '#111827'
    ctx.textBaseline = 'top'
    ctx.textAlign = 'left'
    ctx.fillText(targetName, cardX + 30, cardY + 24)

    ctx.font = '400 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    ctx.fillStyle = '#6B7280'
    ctx.fillText('Daytime population', cardX + 30, cardY + 78)

    ctx.font = '600 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    ctx.fillStyle = '#111827'
    ctx.textAlign = 'right'
    ctx.fillText(formatCompactNumber(population), cardX + cardWidth - 30, cardY + 78)

    if (hasIncome) {
      ctx.textAlign = 'left'
      ctx.font = '400 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      ctx.fillStyle = '#6B7280'
      ctx.fillText('Household income', cardX + 30, cardY + 124)

      ctx.textAlign = 'right'
      ctx.font = '600 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      ctx.fillStyle = '#111827'
      ctx.fillText(`$${formatCompactNumber(income, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`, cardX + cardWidth - 30, cardY + 124)
    }
    ctx.restore()

    return new Promise((resolve) => {
      try {
        canvas.toBlob((blob) => {
          resolve(blob)
        }, 'image/png')
      } catch (err) {
        console.warn('Canvas toBlob error:', err)
        resolve(null)
      }
    })
  }

  const handleSend = async (e) => {
    if (e) e.preventDefault()

    let hasError = false
    const emailRegex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/

    const trimmedFirstName = firstName.trim()
    if (!trimmedFirstName) {
      setFirstNameError('First name is required.')
      hasError = true
    } else {
      setFirstNameError('')
    }

    const trimmedSurname = surname.trim()
    if (!trimmedSurname) {
      setSurnameError('Surname is required.')
      hasError = true
    } else {
      setSurnameError('')
    }

    const trimmedSenderEmail = senderEmail.trim()
    if (!trimmedSenderEmail) {
      setSenderEmailError('Sender email is required.')
      hasError = true
    } else if (!emailRegex.test(trimmedSenderEmail)) {
      setSenderEmailError('Enter a valid email address.')
      hasError = true
    } else {
      setSenderEmailError('')
    }

    const trimmedRecipient = recipientEmail.trim()
    if (!trimmedRecipient) {
      setRecipientError('Recipient email is required.')
      hasError = true
    } else if (!emailRegex.test(trimmedRecipient)) {
      setRecipientError('Enter a valid email address.')
      hasError = true
    } else {
      setRecipientError('')
    }

    const trimmedSubject = subject.trim()
    if (!trimmedSubject) {
      setSubjectError('Subject is required.')
      hasError = true
    } else {
      setSubjectError('')
    }

    const trimmedMessage = message.trim()
    if (!trimmedMessage) {
      setMessageError('Message is required.')
      hasError = true
    } else {
      setMessageError('')
    }

    if (hasError) return

    setIsSending(true)

    try {
      let base64Data = preGeneratedImage || ''
      if (!base64Data) {
        const blob = await generateImageBlob()
        if (blob) {
          base64Data = await new Promise((resolve) => {
            const reader = new FileReader()
            reader.onloadend = () => resolve(reader.result)
            reader.readAsDataURL(blob)
          })
        }
      }

      const geometriesPayload = (geometries || []).map((g) => ({
        id: g.id,
        geoJSON: g.geoJSON,
        color: g.color,
        transparency: g.transparency,
        hasBorder: g.hasBorder,
        borderColor: g.borderColor,
        borderWidth: g.borderWidth,
        borderDashArray: g.borderDashArray,
        borderOpacity: g.borderOpacity,
        isPoint: g.isPoint
      }))

      await api.shareLocationEmail({
        first_name: trimmedFirstName,
        surname: trimmedSurname,
        sender_email: trimmedSenderEmail,
        recipient_email: trimmedRecipient,
        subject: trimmedSubject,
        message: trimmedMessage,
        location_url: window.location.href,
        origin: window.location.origin,
        image: base64Data,
        geometries: geometriesPayload,
        map_state: {
          center: viewport.center,
          zoom: viewport.zoom,
          density_layer: uiStore?.densityLayer || 'none',
          current_year: uiStore?.currentYear || 2016
        },
        target_name: targetName !== 'N/A' ? targetName : '',
        daytime_population: population || 0,
        household_income: income || 0,
        description: (item?.description || item?.properties?.description || rightSidebarStore?.singlePolygonData?.description || '')
      })

      if (typeof onSuccess === 'function') {
        onSuccess({ id: 'email', text: 'Email sent', type: 'check' })
      }
      onClose()
    } catch (err) {
      console.error('Error sharing location by email:', err)
      const resData = err.response && err.response.data
      let generalError = ''
      if (resData) {
        if (Array.isArray(resData.non_field_errors) && resData.non_field_errors[0]) {
          generalError = resData.non_field_errors[0]
        } else if (typeof resData.detail === 'string') {
          generalError = resData.detail
        } else if (typeof resData === 'string') {
          generalError = resData
        }

        if (Array.isArray(resData.first_name) && resData.first_name[0]) {
          setFirstNameError(resData.first_name[0])
        }
        if (Array.isArray(resData.surname) && resData.surname[0]) {
          setSurnameError(resData.surname[0])
        }
        if (Array.isArray(resData.sender_email) && resData.sender_email[0]) {
          setSenderEmailError(resData.sender_email[0])
        }
        if (Array.isArray(resData.recipient_email) && resData.recipient_email[0]) {
          setRecipientError(resData.recipient_email[0])
        }
        if (Array.isArray(resData.subject) && resData.subject[0]) {
          setSubjectError(resData.subject[0])
        }
        if (Array.isArray(resData.message) && resData.message[0]) {
          setMessageError(resData.message[0])
        }
      }

      if (!generalError && (!resData || (!resData.recipient_email && !resData.sender_email && !resData.subject && !resData.message && !resData.first_name && !resData.surname))) {
        generalError = 'Failed to send email. Please try again.'
      }

      setFormError(generalError)
    } finally {
      setIsSending(false)
    }
  }

  if (!isOpen) return null

  const activeBase = BASE_MAP_OPTIONS[3] // Google Hybrid
  const emailHeatmapConfig = resolveHeatmapConfig(uiStore)
  const isEmailHeatmapActive = Boolean(uiStore && uiStore.densityLayer && uiStore.densityLayer !== 'none')

  const renderPointToLayer = (color, name) => (feature, latlng) => {
    const label = name || (feature && feature.properties && (feature.properties.name || feature.properties.title)) || ''
    const labelHtml = label ? `<span class="marker-label leaflet-interactive">${label}</span>` : ''
    const innerColor = (color && color !== '#0a2c4f' && color !== '#102a43' && color !== '#162a3f' && color !== '#18262B' && color !== 'rgba(10, 44, 79, 0.6)') ? color : '#FFFFFF'

    if (typeof L !== 'undefined' && L.divIcon) {
      return L.marker(latlng, {
        icon: L.divIcon({
          className: 'custom-map-marker',
          html: `
            <svg class="map-marker__drop" viewBox="0 0 225.93 225.93" style="color: #102a43; fill: currentColor;">
              <path d="M158.657,43.34c-21.05-24.471-41.786-40.395-42.658-41.061L113.011,0l-3.021,2.234 c-0.873,0.646-21.634,16.104-42.701,40.347c-28.617,32.931-43.127,64.741-43.127,94.547c0,48.966,39.836,88.802,88.802,88.802 s88.803-39.836,88.803-88.802C201.767,108.149,187.262,76.594,158.657,43.34z" />
            </svg>
            <div class="map-marker__icon" style="background-color: ${innerColor}; border-radius: 50%;"></div>
            ${labelHtml}
          `
        })
      })
    }
    return L.circleMarker(latlng, {
      radius: 7,
      fillColor: innerColor,
      color: '#102a43',
      weight: 2,
      opacity: 1,
      fillOpacity: 0.95
    })
  }

  return ReactDOM.createPortal(
    <div
      className='share-image-modal-overlay'
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
    >
      <div
        className='share-email-modal'
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
        onPointerDown={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
        onKeyUp={e => e.stopPropagation()}
      >
        {/* Hidden offscreen map container for rendering tiles and geometry snapshot */}
        <div className='share-email-modal__offscreen-map'>
          <Map
            ref={mapRef}
            center={viewport.center}
            zoom={viewport.zoom}
            dragging={false}
            zoomControl={false}
            scrollWheelZoom={false}
            doubleClickZoom={false}
            touchZoom={false}
            attributionControl={false}
            style={{ width: '960px', height: '540px' }}
          >
            {isEmailHeatmapActive && (
              <WMSTileLayer
                noWrap
                bounds={[[-90, -180], [90, 180]]}
                key={emailHeatmapConfig.key || 'density'}
                url={emailHeatmapConfig.url}
                layers={emailHeatmapConfig.layerId}
                version={emailHeatmapConfig.version}
                format={emailHeatmapConfig.format}
                opacity={0.75}
                zIndex={2}
                transparent
                tiled
                tilesOrigin={[-90, -180]}
              />
            )}
            <GoogleLayer
              key={activeBase.id}
              noWrap={false}
              bounds={[[-90, -180], [90, 180]]}
              apiKey={constants.GOOGLE_KEY}
              type={activeBase.type}
            />
            {geometries.map((g) => (
              <GeoJSON
                key={`${g.id}_${g.color}_${g.borderColor || ''}_${g.borderWidth || ''}_${g.borderDashArray || ''}_${g.transparency}`}
                data={g.geoJSON}
                pointToLayer={renderPointToLayer(g.color, g.name || g.label)}
                style={(feature) => {
                  const featProps = feature?.properties || {}
                  const featBorderColor = featProps.borderColor || featProps.border_color || featProps.stroke || featProps.strokeColor
                  const featBorderWidth = featProps.borderWidth || featProps.border_width || featProps['stroke-width']
                  const featDashArray = featProps.borderDashArray || featProps.dashArray || featProps['stroke-dasharray']

                  const strokeColor = featBorderColor || g.borderColor || (g.hasBorder ? (g.borderColor || '#ff5500') : g.color)
                  const strokeWidth = Number(featBorderWidth || g.borderWidth || (g.hasBorder ? 3 : 2.5))
                  const dashArray = featDashArray || g.borderDashArray || null
                  const strokeOpacity = g.borderOpacity != null ? g.borderOpacity : 1

                  return {
                    color: strokeColor,
                    fillColor: g.color,
                    fillOpacity: g.transparency,
                    opacity: strokeOpacity,
                    weight: strokeWidth,
                    dashArray: dashArray || undefined
                  }
                }}
              />
            ))}
          </Map>
        </div>

        {/* Modal Header */}
        <div className='share-email-modal__header'>
          <h3 className='share-email-modal__title'>Share by email</h3>
          <button
            type='button'
            className='share-image-modal__close-btn'
            onClick={onClose}
            aria-label='Close'
          >
            <CloseIcon />
          </button>
        </div>

        {/* Modal Body */}
        <div className='share-email-modal__body'>
          {formError && (
            <div className='share-email-modal__alert share-email-modal__alert--error'>
              <svg width='16' height='16' viewBox='0 0 16 16' fill='none' stroke='currentColor' strokeWidth='2' style={{ flexShrink: 0 }}>
                <circle cx='8' cy='8' r='7' />
                <line x1='8' y1='5' x2='8' y2='8.5' />
                <circle cx='8' cy='11.5' r='0.5' fill='currentColor' />
              </svg>
              <span>{formError}</span>
            </div>
          )}

          <p className='share-email-modal__subtitle'>
            Confirm the sender details and choose who should receive the shared location.
          </p>

          <div className='share-email-modal__section-title'>
            From
          </div>

          <div className='share-email-modal__row'>
            <div className='share-email-modal__field'>
              <label>First name *</label>
              <input
                type='text'
                value={firstName}
                onChange={e => {
                  setFirstName(e.target.value)
                  if (firstNameError) setFirstNameError('')
                  if (formError) setFormError('')
                }}
                onClick={e => e.stopPropagation()}
                onMouseDown={e => e.stopPropagation()}
                onKeyDown={e => e.stopPropagation()}
                placeholder='First name'
                className={firstNameError ? 'share-email-modal__field--error' : ''}
                disabled={isSending}
              />
              {firstNameError && (
                <span className='share-email-modal__error-text'>{firstNameError}</span>
              )}
            </div>
            <div className='share-email-modal__field'>
              <label>Surname *</label>
              <input
                type='text'
                value={surname}
                onChange={e => {
                  setSurname(e.target.value)
                  if (surnameError) setSurnameError('')
                  if (formError) setFormError('')
                }}
                onClick={e => e.stopPropagation()}
                onMouseDown={e => e.stopPropagation()}
                onKeyDown={e => e.stopPropagation()}
                placeholder='Surname'
                className={surnameError ? 'share-email-modal__field--error' : ''}
                disabled={isSending}
              />
              {surnameError && (
                <span className='share-email-modal__error-text'>{surnameError}</span>
              )}
            </div>
          </div>

          <div className='share-email-modal__field'>
            <label>Sender email *</label>
            <input
              type='email'
              value={senderEmail}
              onChange={e => {
                setSenderEmail(e.target.value)
                if (senderEmailError) setSenderEmailError('')
                if (formError) setFormError('')
              }}
              onClick={e => e.stopPropagation()}
              onMouseDown={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}
              placeholder='Sender email'
              className={senderEmailError ? 'share-email-modal__field--error' : ''}
              disabled={isSending}
            />
            {senderEmailError && (
              <span className='share-email-modal__error-text'>{senderEmailError}</span>
            )}
          </div>

          <div className='share-email-modal__field'>
            <label>Send to *</label>
            <input
              type='email'
              value={recipientEmail}
              onChange={e => {
                setRecipientEmail(e.target.value)
                if (recipientError) setRecipientError('')
                if (formError) setFormError('')
              }}
              onClick={e => e.stopPropagation()}
              onMouseDown={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}
              placeholder='recipient@example.com'
              className={recipientError ? 'share-email-modal__field--error' : ''}
              disabled={isSending}
              autoFocus
            />
            {recipientError && (
              <span className='share-email-modal__error-text'>{recipientError}</span>
            )}
          </div>

          <div className='share-email-modal__field'>
            <label>Subject *</label>
            <input
              type='text'
              value={subject}
              onChange={e => {
                setSubject(e.target.value)
                if (subjectError) setSubjectError('')
                if (formError) setFormError('')
              }}
              onClick={e => e.stopPropagation()}
              onMouseDown={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}
              placeholder='Take a look at this location'
              className={subjectError ? 'share-email-modal__field--error' : ''}
              disabled={isSending}
            />
            {subjectError && (
              <span className='share-email-modal__error-text'>{subjectError}</span>
            )}
          </div>

          <div className='share-email-modal__field'>
            <label>Message *</label>
            <textarea
              value={message}
              onChange={e => {
                setMessage(e.target.value)
                if (messageError) setMessageError('')
                if (formError) setFormError('')
              }}
              onClick={e => e.stopPropagation()}
              onMouseDown={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}
              placeholder='Add a message...'
              rows={3}
              className={messageError ? 'share-email-modal__field--error' : ''}
              disabled={isSending}
            />
            {messageError && (
              <span className='share-email-modal__error-text'>{messageError}</span>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className='share-email-modal__footer'>
          <button
            type='button'
            className='share-image-btn share-image-btn--secondary'
            onClick={onClose}
            disabled={isSending}
          >
            Cancel
          </button>
          <button
            type='button'
            className='share-image-btn share-image-btn--secondary'
            disabled={isSending}
            onClick={handleSend}
          >
            <span className='share-email-modal__send-icon'>
              <MailSendIcon />
            </span>
            {isSending ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default withTranslation()(
  inject('selectionStore', 'dataStore', 'rightSidebarStore', 'uiStore')(
    observer(ShareByEmailModal)
  )
)
